import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { LogOut, LayoutDashboard, Home } from 'lucide-react';

export default function Navbar({ variant = 'public' }) {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [navCta, setNavCta] = useState({ loaded: false, enabled: false, label: '', url: '' });

    useEffect(() => {
        api.get('/share-settings')
            .then((r) => setNavCta({
                loaded: true,
                enabled: r.data.navCtaEnabled === true,
                label: r.data.navCtaLabel || '',
                url: r.data.navCtaUrl || '',
            }))
            .catch(() => setNavCta((prev) => ({ ...prev, loaded: true })));
    }, []);

    const goHome = (e) => {
        e.preventDefault();
        if (location.pathname === '/') {
            // Already on home — scroll to the directory section
            const el = document.querySelector('#recipients');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            else window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/');
        }
    };

    return (
        <header
            data-testid="site-navbar"
            className="sticky top-0 z-40 bg-black/70 backdrop-blur-md border-b border-white/10"
        >
            <div className="max-w-7xl mx-auto px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
                {location.pathname === '/' ? (
                    <div aria-hidden className="shrink-0" />
                ) : (
                    <Link
                        to="/"
                        data-testid="nav-brand-logo"
                        aria-label="UnCloned® Brand Awards — home"
                        className="hero-logo-mask shrink-0 nav-logo"
                        style={{
                            WebkitMaskImage: "url('/uncloned-brand-awards-wordmark.webp')",
                            maskImage: "url('/uncloned-brand-awards-wordmark.webp')",
                        }}
                    />
                )}
                <nav className="flex flex-wrap items-center justify-end gap-1.5 sm:gap-3">
                    {variant === 'admin' ? (
                        <>
                            <NavLink
                                to="/"
                                data-testid="nav-admin-view-site"
                                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full text-white/70 hover:text-white transition-colors"
                                title="Open the public site"
                            >
                                <Home size={16} /> View site
                            </NavLink>
                            <NavLink
                                to="/admin"
                                end
                                data-testid="nav-admin-dashboard"
                                className={({ isActive }) =>
                                    `hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm rounded-full transition-colors ${
                                        isActive ? 'text-un-lime' : 'text-white/70 hover:text-white'
                                    }`
                                }
                            >
                                <LayoutDashboard size={16} /> Dashboard
                            </NavLink>
                            <NavLink
                                to="/admin/share-settings"
                                data-testid="nav-admin-share-settings"
                                className={({ isActive }) =>
                                    `hidden sm:inline-flex px-3 py-2 text-sm rounded-full transition-colors ${
                                        isActive ? 'text-un-lime' : 'text-white/70 hover:text-white'
                                    }`
                                }
                            >
                                Share preview
                            </NavLink>
                            <NavLink
                                to="/admin/settings"
                                data-testid="nav-admin-settings"
                                className={({ isActive }) =>
                                    `hidden sm:inline-flex px-3 py-2 text-sm rounded-full transition-colors ${
                                        isActive ? 'text-un-lime' : 'text-white/70 hover:text-white'
                                    }`
                                }
                            >
                                Settings
                            </NavLink>
                            <button
                                data-testid="nav-logout-btn"
                                onClick={async () => { await logout(); navigate('/admin/login'); }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-sm font-semibold hover:bg-un-lime transition-colors"
                            >
                                <LogOut size={16} /> Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            {navCta.loaded && navCta.enabled && navCta.url ? (
                                <a
                                    href={navCta.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="nav-community-link"
                                    className="inline-flex items-center px-3 py-1.5 rounded-full bg-un-lime text-black text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold shadow-[0_0_0_1px_rgba(208,255,13,0.4),0_10px_30px_-8px_rgba(208,255,13,0.55)] hover:bg-white transition-colors whitespace-nowrap"
                                >
                                    {navCta.label}
                                </a>
                            ) : null}
                            <a
                                href="/"
                                onClick={goHome}
                                data-testid="nav-recipients-link"
                                className="text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold text-white/70 hover:text-white transition-colors px-2.5 py-1.5 cursor-pointer"
                            >
                                Recipients
                            </a>
                            {admin ? (
                                <Link
                                    to="/admin"
                                    data-testid="nav-admin-link"
                                    className="hidden sm:inline-flex px-3 py-1.5 rounded-full border border-white/20 text-white text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold hover:border-un-water hover:text-un-water transition-colors"
                                >
                                    Admin
                                </Link>
                            ) : (
                                <Link
                                    to="/admin/login"
                                    data-testid="nav-login-link"
                                    className="hidden sm:inline-flex px-3 py-1.5 rounded-full border border-white/20 text-white text-[11px] sm:text-xs uppercase tracking-[0.14em] font-bold hover:border-un-water hover:text-un-water transition-colors"
                                >
                                    Admin login
                                </Link>
                            )}
                        </>
                    )}
                </nav>
            </div>
        </header>
    );
}
